import { useQuery } from '@tanstack/react-query';
import {
  Ban,
  CalendarCheck2,
  CalendarPlus,
  Copy,
  Eye,
  Flag,
  ListFilter,
  MoreVertical,
  Network,
  Pencil,
  Play,
  Plus,
  Search,
  Shuffle,
  Trash2,
  Trophy,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  activeFilterCount,
  canCopyTournament,
  COMPETITION_CATEGORY_LABELS,
  competitionSystemLabel,
  competitionTypeLabel,
  COMPETITION_STATUS_LABELS,
  COMPETITION_STATUS_TONE,
  copySuggestedName,
  countByFilter,
  daysUntil,
  DEFAULT_TOURNAMENT_FILTERS,
  DISCIPLINE_CODE_LABELS,
  drawKindOf,
  filterTournaments,
  formatDateRange,
  indexTournamentProgress,
  PAGE_SIZES,
  paginate,
  REGISTRATION_LABELS,
  REGISTRATION_TONE,
  registrationState,
  relativeDayLabel,
  TOURNAMENT_FILTERS,
  TournamentDetailsModal,
  TournamentIcon,
  tournamentSubtitle,
  useClubCompetitionsQuery,
  useClubDashboardQuery,
  useCompetitionActionMutation,
  useCopyCompetitionMutation,
  useSetVisibilityMutation,
  type ApiCompetition,
  type CompetitionActionVariables,
  type DisciplineCode,
  type TournamentFilterKey,
  type TournamentListFilters,
} from '@/features/competition';
import { fullNameOf, getUsersByIds, userKeys } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Badge,
  Button,
  Card,
  DropdownMenu,
  EmptyState,
  FormField,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Pagination,
  ProgressBar,
  Select,
  Skeleton,
  StatusDot,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  useToast,
  type DropdownMenuItem,
  type TabItem,
} from '@/shared/ui';

/** Klucze etykiet zakładek — wypisane wprost, bo `t()` wymaga literału. */
const TAB_LABEL_KEY = {
  all: 'list.tabs.all',
  active: 'list.tabs.active',
  upcoming: 'list.tabs.upcoming',
  completed: 'list.tabs.completed',
  draft: 'list.tabs.draft',
  cancelled: 'list.tabs.cancelled',
} as const satisfies Record<TournamentFilterKey, string>;

/** Komunikat po udanej zmianie stanu rozgrywki. */
const TOAST_KEY = {
  'open-registration': 'list.toast.openRegistration',
  'close-registration': 'list.toast.closeRegistration',
  start: 'list.toast.start',
  complete: 'list.toast.complete',
  cancel: 'list.toast.cancel',
  delete: 'list.toast.delete',
} as const satisfies Record<CompetitionActionVariables['action'], string>;

/** Statusy, z których nie ma już dokąd przejść. */
const CLOSED_STATUSES = new Set(['COMPLETED', 'CANCELLED']);

/**
 * Centrum turniejów klubu — pełna lista rozgrywek z filtrami, wyszukiwaniem,
 * stronicowaniem i akcjami cyklu życia, według makiety `turnieje_glowna.png`.
 *
 * Dane pochodzą z `GET /api/competitions?clubId=…`, które zwraca całą listę
 * naraz — filtrowanie i stronicowanie robimy więc po stronie klienta.
 */
export function TournamentsPage() {
  const { t } = useTranslation('tournament');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { clubId, isEmpty: hasNoClubs } = useSelectedClub();

  const competitionsQuery = useClubCompetitionsQuery(clubId);
  const dashboardQuery = useClubDashboardQuery(clubId);
  const actionMutation = useCompetitionActionMutation(clubId);
  const copyMutation = useCopyCompetitionMutation(clubId);
  const visibilityMutation = useSetVisibilityMutation(clubId);

  const [filters, setFilters] = useState<TournamentListFilters>(DEFAULT_TOURNAMENT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [preview, setPreview] = useState<ApiCompetition | null>(null);
  const [pendingCancel, setPendingCancel] = useState<ApiCompetition | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ApiCompetition | null>(null);
  const [pendingCopy, setPendingCopy] = useState<ApiCompetition | null>(null);
  const [copyName, setCopyName] = useState('');

  const competitions = useMemo(() => competitionsQuery.data ?? [], [competitionsQuery.data]);
  const counts = useMemo(() => countByFilter(competitions), [competitions]);
  const visible = useMemo(() => filterTournaments(competitions, filters), [competitions, filters]);
  const slice = useMemo(() => paginate(visible, page, pageSize), [visible, page, pageSize]);

  // Ani liczba zapisanych, ani postęp meczów nie wracają z listy rozgrywek —
  // dokładamy je z agregatu dashboardu, który zna tylko rozgrywki trwające
  // i zaplanowane.
  const progressByCompetition = useMemo(
    () => indexTournamentProgress(dashboardQuery.data?.tournaments),
    [dashboardQuery.data],
  );

  // Nazwiska organizatorów jednym zapytaniem dla całej listy — nie na stronę,
  // żeby przewijanie stron nie odpalało kolejnych żądań.
  const ownerIds = useMemo(
    () => [...new Set(competitions.map((item) => item.ownerId))].sort(),
    [competitions],
  );
  const ownersQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', ownerIds.join(',')],
    queryFn: () => getUsersByIds(ownerIds),
    enabled: ownerIds.length > 0,
  });
  const ownerNames = useMemo(
    () => new Map((ownersQuery.data ?? []).map((user) => [user.id, fullNameOf(user)])),
    [ownersQuery.data],
  );

  // Listy filtrów budujemy z danych — nie pokazujemy dyscyplin ani kategorii,
  // których ten klub w ogóle nie używa.
  const availableDisciplines = useMemo(
    () => [...new Set(competitions.map((item) => item.discipline))].sort(),
    [competitions],
  );
  const availableCategories = useMemo(
    () =>
      [
        ...new Set(competitions.map((item) => item.category).filter((item) => item !== null)),
      ].sort(),
    [competitions],
  );

  const today = new Date();
  const extraFilters = activeFilterCount(filters);

  /** Zmienia filtry i cofa widok na pierwszą stronę. */
  function updateFilters(patch: Partial<TournamentListFilters>) {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  }

  function runAction(competition: ApiCompetition, action: CompetitionActionVariables['action']) {
    actionMutation.mutate(
      { competitionId: competition.id, action },
      {
        onSuccess: () => {
          setPendingCancel(null);
          setPendingDelete(null);
          showToast({
            tone: 'success',
            description: t(TOAST_KEY[action], { name: competition.name }),
          });
        },
        onError: (error) => {
          showToast({
            tone: 'danger',
            description: error instanceof ApiError ? error.message : t('list.toast.error'),
          });
        },
      },
    );
  }

  /** Przełącza widoczność rozgrywki wprost z listy — dozwolone w każdym statusie. */
  function toggleVisibility(competition: ApiCompetition) {
    const visibility = competition.visibility === 'PUBLIC' ? 'PRIVATE' : 'PUBLIC';
    visibilityMutation.mutate(
      { competitionId: competition.id, visibility },
      {
        onSuccess: () => {
          showToast({
            tone: 'success',
            description: t(
              visibility === 'PUBLIC'
                ? 'list.toast.visibilityPublic'
                : 'list.toast.visibilityPrivate',
              { name: competition.name },
            ),
          });
        },
        onError: (error) => {
          showToast({
            tone: 'danger',
            description: error instanceof ApiError ? error.message : t('list.toast.error'),
          });
        },
      },
    );
  }

  /** Otwiera okno kopiowania z podpowiedzianą nazwą kopii. */
  function startCopy(competition: ApiCompetition) {
    setPendingCopy(competition);
    setCopyName(copySuggestedName(competition.name));
  }

  /** Zleca kopiowanie turnieju pod nazwą wpisaną w oknie. */
  function confirmCopy() {
    if (!pendingCopy) return;
    const name = copyName.trim();
    if (!name) return;

    copyMutation.mutate(
      { competitionId: pendingCopy.id, name },
      {
        onSuccess: () => {
          setPendingCopy(null);
          showToast({ tone: 'success', description: t('list.toast.copy', { name }) });
        },
        onError: (error) => {
          showToast({
            tone: 'danger',
            description: error instanceof ApiError ? error.message : t('list.toast.error'),
          });
        },
      },
    );
  }

  /** Buduje menu akcji dostępnych dla bieżącego statusu rozgrywki. */
  function actionsFor(competition: ApiCompetition): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [
      {
        label: t('list.actions.participants'),
        icon: <Users />,
        onSelect: () => void navigate(`/turnieje/${competition.id}/zawodnicy`),
      },
    ];

    // Edycja ustawień dozwolona do losowania (backend odrzuca ją od IN_PROGRESS).
    const editableStatuses = new Set([
      'DRAFT',
      'AWAITING_PAYMENT',
      'REGISTRATION_OPEN',
      'REGISTRATION_CLOSED',
    ]);
    if (editableStatuses.has(competition.status)) {
      items.push({
        label: t('list.actions.edit'),
        icon: <Pencil />,
        onSelect: () => void navigate(`/turnieje/${competition.id}/edycja`),
      });
    }

    // Kopiowanie pojawia się dopiero z otwarciem zapisów — do tego momentu
    // organizator wciąż konfiguruje oryginał, więc nie ma czego powielać.
    if (canCopyTournament(competition.status)) {
      items.push({
        label: t('list.actions.copy'),
        icon: <Copy />,
        onSelect: () => startCopy(competition),
      });
    }

    // Klucz etykiety podglądu wyniku losowania — zależny od systemu rozgrywek.
    const drawKind = drawKindOf(competition.system);
    const viewDrawItem: DropdownMenuItem = {
      label: t(
        drawKind === 'bracket'
          ? 'list.actions.viewBracket'
          : drawKind === 'groups'
            ? 'list.actions.viewGroups'
            : 'list.actions.viewSchedule',
      ),
      icon: <Network />,
      onSelect: () => void navigate(`/turnieje/${competition.id}/drabinka`),
    };

    switch (competition.status) {
      case 'DRAFT':
        items.push({
          label: t('list.actions.openRegistration'),
          icon: <CalendarPlus />,
          onSelect: () => runAction(competition, 'open-registration'),
        });
        break;
      case 'REGISTRATION_OPEN':
        items.push({
          label: t('list.actions.closeRegistration'),
          icon: <CalendarCheck2 />,
          onSelect: () => runAction(competition, 'close-registration'),
        });
        break;
      case 'REGISTRATION_CLOSED':
        // Losowanie samo startuje rozgrywkę (draw wymaga REGISTRATION_CLOSED),
        // więc surowe „Rozpocznij” zostaje tylko dla systemów bez losowania —
        // wystartowanie bez losowania zablokowałoby draw na zawsze.
        if (drawKind !== 'none') {
          items.push({
            label: t('list.actions.draw'),
            icon: <Shuffle />,
            onSelect: () => void navigate(`/turnieje/${competition.id}/losowanie`),
          });
        } else {
          items.push({
            label: t('list.actions.start'),
            icon: <Play />,
            onSelect: () => runAction(competition, 'start'),
          });
        }
        break;
      case 'IN_PROGRESS':
        if (drawKind !== 'none') items.push(viewDrawItem);
        items.push({
          label: t('list.actions.complete'),
          icon: <Flag />,
          onSelect: () => runAction(competition, 'complete'),
        });
        break;
      case 'COMPLETED':
        if (drawKind !== 'none') items.push(viewDrawItem);
        break;
      default:
        break;
    }

    if (!CLOSED_STATUSES.has(competition.status)) {
      items.push({
        label: t('list.actions.cancel'),
        icon: <Ban />,
        destructive: true,
        onSelect: () => setPendingCancel(competition),
      });
    }

    // Miękkie usunięcie dostępne zawsze — turniej znika ze wszystkich widoków,
    // ale zostaje w bazie.
    items.push({
      label: t('list.actions.delete'),
      icon: <Trash2 />,
      destructive: true,
      onSelect: () => setPendingDelete(competition),
    });

    return items;
  }

  const tabs: TabItem<TournamentFilterKey>[] = TOURNAMENT_FILTERS.map((key) => ({
    value: key,
    label: (
      <>
        {t(TAB_LABEL_KEY[key])}
        <span className="rounded-full bg-current/15 px-1.5 py-0.5 text-[0.6875rem] tabular-nums">
          {counts[key]}
        </span>
      </>
    ),
  }));

  if (hasNoClubs) {
    return (
      <>
        <PageHeader title={t('list.title')} description={t('list.description')} />
        <EmptyState
          icon={<Trophy />}
          title={t('list.noClubsTitle')}
          description={t('list.noClubsDescription')}
          className="min-h-[24rem]"
        />
      </>
    );
  }

  /** Główna akcja obszaru — ten sam przycisk w nagłówku i w pustym stanie. */
  const newTournamentButton = (
    <Button
      leadingIcon={<Plus aria-hidden="true" />}
      onClick={() => void navigate('/turnieje/nowy')}
    >
      {tCommon('actions.newTournament')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        actions={newTournamentButton}
      />

      {competitionsQuery.isError && <Alert tone="danger">{t('list.loadError')}</Alert>}

      <Card padding="sm" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs
            items={tabs}
            value={filters.filter}
            onValueChange={(value) => updateFilters({ filter: value })}
            label={t('list.title')}
          />

          <div className="flex items-center gap-2">
            <Input
              inputSize="sm"
              type="search"
              aria-label={t('list.search')}
              placeholder={t('list.search')}
              leadingIcon={<Search />}
              value={filters.search}
              onChange={(event) => updateFilters({ search: event.target.value })}
              className="w-56"
            />
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<ListFilter aria-hidden="true" />}
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((current) => !current)}
            >
              {t('list.filters')}
              {extraFilters > 0 && (
                <Badge tone="brand" size="sm" className="ml-1">
                  {extraFilters}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {filtersOpen && (
          <div className="flex flex-wrap items-end gap-3 border-t border-border pt-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">
                {t('list.filterDiscipline')}
              </span>
              <Select
                selectSize="sm"
                value={filters.discipline}
                onChange={(event) =>
                  updateFilters({ discipline: event.target.value as DisciplineCode | 'all' })
                }
                className="w-44"
              >
                <option value="all">{t('list.filterAll')}</option>
                {availableDisciplines.map((discipline) => (
                  <option key={discipline} value={discipline}>
                    {DISCIPLINE_CODE_LABELS[discipline] ?? discipline}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{t('list.filterCategory')}</span>
              <Select
                selectSize="sm"
                value={filters.category}
                onChange={(event) => updateFilters({ category: event.target.value })}
                className="w-44"
              >
                <option value="all">{t('list.filterAll')}</option>
                {availableCategories.map((category) => (
                  <option key={category} value={category}>
                    {COMPETITION_CATEGORY_LABELS[category] ?? category}
                  </option>
                ))}
              </Select>
            </label>

            {extraFilters > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateFilters({ discipline: 'all', category: 'all' })}
              >
                {t('list.filtersClear')}
              </Button>
            )}
          </div>
        )}
      </Card>

      {competitionsQuery.isPending && clubId && <Skeleton className="h-72 w-full" />}

      {competitionsQuery.isSuccess && competitions.length === 0 && (
        <EmptyState
          icon={<Trophy />}
          title={t('list.emptyTitle')}
          description={t('list.emptyDescription')}
          action={newTournamentButton}
          className="min-h-[18rem]"
        />
      )}

      {competitions.length > 0 && visible.length === 0 && (
        <EmptyState
          icon={<Search />}
          title={t('list.emptyFilteredTitle')}
          description={t('list.emptyFilteredDescription')}
          className="min-h-[18rem]"
        />
      )}

      {slice.items.length > 0 && (
        <>
          <TableContainer>
            {/* Ciasniejsze wciecia komorek — dziewięć kolumn ma zmiescic sie bez
                poziomego przewijania na typowym ekranie. */}
            <Table className="[&_td]:px-3 [&_th]:px-3">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t('list.columns.tournament')}</TableHeaderCell>
                  <TableHeaderCell>{t('list.columns.term')}</TableHeaderCell>
                  <TableHeaderCell>{t('list.columns.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('list.columns.kind')}</TableHeaderCell>
                  <TableHeaderCell>{t('list.columns.participants')}</TableHeaderCell>
                  <TableHeaderCell>{t('list.columns.registration')}</TableHeaderCell>
                  <TableHeaderCell>{t('list.columns.progress')}</TableHeaderCell>
                  <TableHeaderCell>{t('list.columns.visibility')}</TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    {t('list.columns.actions')}
                  </TableHeaderCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {slice.items.map((competition) => {
                  const startsIn = relativeDayLabel(daysUntil(competition.startDate, today));
                  const stats = progressByCompetition.get(competition.id);
                  const participants = stats?.participantCount;
                  const registration = registrationState(competition.status);
                  const menuItems = actionsFor(competition);

                  return (
                    <TableRow
                      key={competition.id}
                      className="cursor-pointer transition-colors hover:bg-surface-hover"
                      onClick={() => void navigate(`/turnieje/${competition.id}/informacje`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <TournamentIcon status={competition.status} className="size-10" />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-fg-strong">
                              {competition.name}
                            </p>
                            <p className="truncate text-xs text-fg-muted">
                              {tournamentSubtitle(competition)}
                            </p>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <p className="text-sm text-fg">
                          {formatDateRange(competition.startDate, competition.endDate)}
                        </p>
                        {startsIn && <p className="text-xs text-brand-text">{startsIn}</p>}
                      </TableCell>

                      <TableCell>
                        <Badge tone={COMPETITION_STATUS_TONE[competition.status]} size="sm">
                          {COMPETITION_STATUS_LABELS[competition.status] ?? competition.status}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <p className="text-sm whitespace-nowrap text-fg">
                          {competitionTypeLabel(competition)}
                        </p>
                        <p className="text-xs text-fg-muted">
                          {competitionSystemLabel(competition)}
                        </p>
                      </TableCell>

                      <TableCell className="w-28">
                        {participants === undefined ? (
                          <span
                            className="text-sm text-fg-subtle"
                            title={t('list.participantsUnknown')}
                          >
                            {/* Po zamknięciu zapisów limit nie ma już znaczenia. */}
                            {registration === 'closed' ? '—' : `— / ${competition.maxPlayers}`}
                          </span>
                        ) : registration === 'closed' ? (
                          // Zapisy zamknięte — liczy się realna obsada turnieju,
                          // a nie stopień zapełnienia limitu.
                          <span
                            className="flex items-center gap-1.5 text-sm font-semibold text-fg"
                            title={t('list.participantsTaking')}
                          >
                            <Users className="size-4 text-fg-subtle" aria-hidden="true" />
                            {participants}
                          </span>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            <span className="flex items-center gap-1.5 text-sm text-fg">
                              <Users className="size-4 text-fg-subtle" aria-hidden="true" />
                              {participants} / {competition.maxPlayers}
                            </span>
                            <ProgressBar
                              size="sm"
                              value={participants}
                              max={competition.maxPlayers}
                            />
                          </div>
                        )}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <Badge tone={REGISTRATION_TONE[registration]} size="sm">
                          {registration === 'open' && (
                            <StatusDot status="finished" size="sm" aria-hidden="true" />
                          )}
                          {REGISTRATION_LABELS[registration]}
                        </Badge>
                      </TableCell>

                      <TableCell className="w-32">
                        {/* Postęp pokazujemy dla rozgrywek w toku i zakończonych —
                            przy tych drugich pasek domyka się na 100% i mówi, ile
                            meczów finalnie rozegrano. Szkice, zapisy i anulowane
                            nie mają czego mierzyć. */}
                        {!stats ||
                        (competition.status !== 'IN_PROGRESS' &&
                          competition.status !== 'COMPLETED') ? (
                          <span className="text-sm text-fg-subtle">—</span>
                        ) : stats.matchesTotal === 0 ? (
                          // Rozgrywka wystartowana bez losowania — nie ma czego mierzyć.
                          <span className="text-xs text-fg-subtle">{t('list.progressNoDraw')}</span>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            <span className="text-xs text-fg">
                              {t('list.progressMatches', {
                                played: stats.matchesPlayed,
                                total: stats.matchesTotal,
                              })}
                            </span>
                            <ProgressBar
                              size="sm"
                              value={stats.matchesPlayed}
                              max={stats.matchesTotal}
                            />
                          </div>
                        )}
                      </TableCell>

                      <TableCell
                        className="whitespace-nowrap"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {/* Widoczność można przełączać w każdym statusie —
                            klik w przełącznik nie ma nawigować do turnieju. */}
                        <div className="flex flex-col items-start gap-1">
                          <Switch
                            aria-label={t('list.visibilityAria', { name: competition.name })}
                            checked={competition.visibility === 'PUBLIC'}
                            disabled={
                              visibilityMutation.isPending &&
                              visibilityMutation.variables?.competitionId === competition.id
                            }
                            onChange={() => toggleVisibility(competition)}
                          />
                          <span className="text-xs text-fg-subtle">
                            {competition.visibility === 'PUBLIC'
                              ? t('list.visibilityPublic')
                              : t('list.visibilityPrivate')}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell>
                        {/* Wiersz nawiguje do centrum turnieju — akcje w tej
                            kolumnie nie mają tam prowadzić. */}
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <IconButton
                            variant="ghost"
                            size="sm"
                            icon={<Eye />}
                            label={t('list.actions.preview')}
                            onClick={() => setPreview(competition)}
                          />
                          {menuItems.length > 0 ? (
                            <DropdownMenu
                              items={menuItems}
                              trigger={(triggerProps) => (
                                <IconButton
                                  {...triggerProps}
                                  variant="ghost"
                                  size="sm"
                                  icon={<MoreVertical />}
                                  label={t('list.actions.more')}
                                />
                              )}
                            />
                          ) : (
                            <IconButton
                              variant="ghost"
                              size="sm"
                              icon={<MoreVertical />}
                              label={t('list.actions.more')}
                              disabled
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-fg-muted">
              {t('list.perPage')}
              <Select
                selectSize="sm"
                value={String(pageSize)}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                className={cn('w-20')}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </label>

            <Pagination page={slice.page} pageCount={slice.pageCount} onPageChange={setPage} />

            <p className="text-sm text-fg-subtle tabular-nums">
              {t('list.range', { from: slice.from, to: slice.to, total: slice.total })}
            </p>
          </div>
        </>
      )}

      <TournamentDetailsModal
        competition={preview}
        onClose={() => setPreview(null)}
        participantCount={
          preview ? progressByCompetition.get(preview.id)?.participantCount : undefined
        }
        ownerName={preview ? ownerNames.get(preview.ownerId) : undefined}
      />

      <Modal
        open={pendingCopy !== null}
        onClose={() => setPendingCopy(null)}
        title={t('list.copyDialog.title')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingCopy(null)}>
              {t('list.copyDialog.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={copyMutation.isPending}
              disabled={copyName.trim().length === 0}
              onClick={confirmCopy}
            >
              {t('list.copyDialog.confirm')}
            </Button>
          </>
        }
      >
        {pendingCopy && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-fg-muted">
              {t('list.copyDialog.body', { name: pendingCopy.name })}
            </p>
            <FormField label={t('list.copyDialog.nameLabel')} required>
              <Input
                value={copyName}
                autoFocus
                onChange={(event) => setCopyName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') confirmCopy();
                }}
              />
            </FormField>
          </div>
        )}
      </Modal>

      <Modal
        open={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        title={t('list.confirmCancel.title')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingCancel(null)}>
              {t('list.confirmCancel.keep')}
            </Button>
            <Button
              variant="danger"
              loading={actionMutation.isPending}
              onClick={() => pendingCancel && runAction(pendingCancel, 'cancel')}
            >
              {t('list.confirmCancel.confirm')}
            </Button>
          </>
        }
      >
        {pendingCancel && t('list.confirmCancel.body', { name: pendingCancel.name })}
      </Modal>

      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={t('list.confirmDelete.title')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
              {t('list.confirmDelete.keep')}
            </Button>
            <Button
              variant="danger"
              loading={actionMutation.isPending}
              onClick={() => pendingDelete && runAction(pendingDelete, 'delete')}
            >
              {t('list.confirmDelete.confirm')}
            </Button>
          </>
        }
      >
        {pendingDelete && t('list.confirmDelete.body', { name: pendingDelete.name })}
      </Modal>
    </div>
  );
}
